/**
 * HTML Sanitizer
 * Uses DOMPurify to prevent XSS vulnerabilities in rendered HTML content
 * Applies strict configuration to only allow safe HTML elements and attributes
 */

import DOMPurify from 'dompurify';

// Configure DOMPurify with strict settings
const SANITIZER_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'div', 'span', 'br', 'hr',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'b', 'em', 'i', 'u', 'mark', 'small', 'sub', 'sup',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    'blockquote', 'pre', 'code',
    'img', 'a',
    'section', 'article', 'aside', 'nav', 'main',
    'form', 'input', 'textarea', 'button', 'select', 'option',
    'label', 'fieldset', 'legend',
    'dl', 'dt', 'dd',
    'figure', 'figcaption'
  ],
  ALLOWED_ATTR: [
    'class', 'id', 'style',
    'href', 'target', 'rel',
    'src', 'alt', 'title', 'width', 'height',
    'colspan', 'rowspan', 'type', 'name', 'value', 'placeholder',
    'disabled', 'readonly', 'checked', 'selected',
    'aria-label', 'aria-describedby', 'role',
    'data-*'
  ],
  KEEP_CONTENT: true,
  FORCE_BODY: false,
  RETURN_DOM: false,
};

/**
 * Sanitize HTML string to prevent XSS attacks
 * Strips potentially dangerous tags and attributes while preserving safe content
 */
export function sanitizeHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    return DOMPurify.sanitize(html, SANITIZER_CONFIG);
  } catch (error) {
    console.error('[htmlSanitizer] Error sanitizing HTML:', error);
    return '';
  }
}

/**
 * Sanitize HTML and escape any remaining potentially harmful content
 * More aggressive approach for user-generated content
 */
export function sanitizeHTMLStrict(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    // First pass: sanitize with DOMPurify
    let sanitized = DOMPurify.sanitize(html, {
      ...SANITIZER_CONFIG,
      ALLOWED_TAGS: [
        'p', 'div', 'span', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
        'blockquote', 'pre', 'code', 'img', 'a', 'hr'
      ],
      ALLOWED_ATTR: ['class', 'id', 'href', 'src', 'alt', 'title']
    });

    return sanitized;
  } catch (error) {
    console.error('[htmlSanitizer] Error sanitizing HTML (strict):', error);
    return '';
  }
}

/**
 * Escape HTML special characters (alternative approach if sanitization fails)
 * Only escapes structurally dangerous HTML characters, not quotes/apostrophes
 * which corrupt stored text data.
 */
export function escapeHTML(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
  };

  return text.replace(/[&<>"]/g, (char) => map[char]);
}

/**
 * Remove all HTML tags from text
 */
export function stripHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    return div.textContent || div.innerText || '';
  } catch {
    return '';
  }
}

/**
 * Sanitize text content for safe display (removes HTML entirely)
 */
export function sanitizeTextContent(content: string): string {
  if (!content || typeof content !== 'string') {
    return '';
  }

  return escapeHTML(content);
}

/**
 * Check if HTML contains potentially malicious content
 */
export function containsSuspiciousContent(html: string): boolean {
  if (!html || typeof html !== 'string') {
    return false;
  }

  // Check for common XSS vectors
  const suspiciousPatterns = [
    /<script[^>]*>[\s\S]*?<\/script>/gi,
    /on\w+\s*=/gi, // Event handlers (onclick, onerror, etc)
    /javascript:/gi,
    /vbscript:/gi,
    /<iframe[^>]*>/gi,
    /<embed[^>]*>/gi,
    /<object[^>]*>/gi,
  ];

  return suspiciousPatterns.some(pattern => pattern.test(html));
}

/**
 * Sanitize attribute value (e.g., for href, src, etc)
 */
export function sanitizeAttributeValue(value: string, attributeName: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const lowerValue = value.toLowerCase().trim();

  // Prevent javascript: and data: URLs in href/src attributes
  if ((attributeName === 'href' || attributeName === 'src') &&
      (lowerValue.startsWith('javascript:') || 
       lowerValue.startsWith('data:') ||
       lowerValue.startsWith('vbscript:'))) {
    return '';
  }

  return value;
}

/**
 * Create a safe text node from potentially unsafe HTML
 */
export function createSafeTextNode(content: string): Text {
  if (!content || typeof content !== 'string') {
    return document.createTextNode('');
  }

  return document.createTextNode(content);
}
